/**
 * MessageList component for displaying admin messages.
 */

import {
  Box,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  Collapse,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useState } from 'react';

import type { JudgeMessage } from '@/types';

interface MessageListProps {
  messages: JudgeMessage[];
  maxVisible?: number;
}

export function MessageList({ messages, maxVisible = 3 }: MessageListProps) {
  const [showAll, setShowAll] = useState(false);

  if (messages.length === 0) {
    return null;
  }

  const visibleMessages = showAll ? messages : messages.slice(0, maxVisible);
  const hiddenCount = messages.length - maxVisible;

  const getAlertSeverity = (status: JudgeMessage['status']) => {
    switch (status) {
      case 'ok':
        return 'success';
      case 'fail':
        return 'error';
      case 'info':
      default:
        return 'info';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fi-FI', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Viestit
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {visibleMessages.map((msg) => (
          <Alert
            key={msg.id}
            severity={getAlertSeverity(msg.status)}
            sx={{ py: 0.5 }}
          >
            <AlertTitle sx={{ fontSize: '0.875rem', mb: 0.5 }}>
              {formatTimestamp(msg.timestamp)}
            </AlertTitle>
            <Typography
              variant="body2"
              dangerouslySetInnerHTML={{ __html: msg.message }}
            />
          </Alert>
        ))}

        {hiddenCount > 0 && (
          <Collapse in={showAll}>
            {messages.slice(maxVisible).map((msg) => (
              <Alert
                key={msg.id}
                severity={getAlertSeverity(msg.status)}
                sx={{ py: 0.5, mt: 1 }}
              >
                <AlertTitle sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                  {formatTimestamp(msg.timestamp)}
                </AlertTitle>
                <Typography
                  variant="body2"
                  dangerouslySetInnerHTML={{ __html: msg.message }}
                />
              </Alert>
            ))}
          </Collapse>
        )}

        {hiddenCount > 0 && (
          <Button
            size="small"
            onClick={() => setShowAll(!showAll)}
            endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {showAll ? 'Näytä vähemmän' : `Näytä lisää (${hiddenCount})`}
          </Button>
        )}
      </Box>
    </Paper>
  );
}


